import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Search, Edit2, Trash2, ChevronDown, ChevronUp, X, Trash, AlertTriangle } from 'lucide-react';
import { authFetch } from '../../lib/auth-fetch';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = ['usp', 'produkt', 'technik', 'fehler', 'montage', 'support', 'vertrieb', 'faq'];
const FAMILIES = ['alle', 'LS', 'HS', 'HS PV', 'HS PV duo', 'HS GenLink', 'GS', 'GS HV', 'GS-C HVP'];
const VISIBILITIES = ['intern', 'extern', 'support', 'vertrieb'];
const SAFETY_LEVELS = [null, 'ENDUSER_SAFE', 'QUALIFIED_PERSONNEL_REQUIRED'];

const emptyEntry = () => ({ title: '', category: 'produkt', productFamily: 'alle', modelCodes: '', tags: '', content: '', visibility: 'intern', safetyLevel: null, status: 'active' });

export default function KnowledgeBase() {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'customer';
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState({ category: '', search: '', tag: '' });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEntry());
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter.category) params.set('category', filter.category);
    if (filter.tag)      params.set('tag', filter.tag);
    const res = await authFetch(`/api/knowledge/?${params}`);
    if (res.ok) setEntries(await res.json());
    setLoading(false);
  }, [filter.category, filter.tag]);

  useEffect(() => {
    const t = setTimeout(() => load(), 0);
    return () => clearTimeout(t);
  }, [load]);

  // Alle Tags, die in der aktuellen Ergebnismenge vorkommen — als Dropdown-
  // Quelle. Sortiert nach Häufigkeit (häufigste zuerst), damit das nützliche
  // Tags wie „hans-korrektur" oder „fehler" oben stehen.
  const availableTags = useMemo(() => {
    const counts = new Map();
    for (const e of entries) {
      for (const t of (e.tags || [])) {
        counts.set(t, (counts.get(t) || 0) + 1);
      }
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [entries]);

  const filtered = entries.filter(e => {
    if (!filter.search) return true;
    const q = filter.search.toLowerCase();
    const inTitle = (e.title || '').toLowerCase().includes(q);
    const inContent = (e.content || '').toLowerCase().includes(q);
    const inTags = (e.tags || []).some(t => t.toLowerCase().includes(q));
    return inTitle || inContent || inTags;
  });

  const save = async () => {
    const body = {
      ...form,
      tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags,
      modelCodes: typeof form.modelCodes === 'string'
        ? form.modelCodes.split(',').map(t => t.trim()).filter(Boolean)
        : (form.modelCodes || []),
    };
    const isNew = !editing;
    const url = isNew ? `/api/knowledge/` : `/api/knowledge/${editing}`;
    const res = await authFetch(url, { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (res.ok) { setEditing(null); setForm(emptyEntry()); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    await authFetch(`/api/knowledge/${id}`, { method: 'DELETE' });
    load();
  };

  const startEdit = (entry) => {
    setEditing(entry.id);
    setForm({ ...entry, tags: (entry.tags || []).join(', '), modelCodes: (entry.modelCodes || []).join(', ') });
  };

  const startNew = () => { setEditing('new'); setForm(emptyEntry()); };

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-[#32373c]" data-testid="kb-title">Wissensdatenbank</h1>
          <p className="text-xs text-gray-400">{entries.length} Einträge</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (
            <Button
              onClick={async () => {
                const r1 = await authFetch('/api/knowledge/_/cleanup-empty?dry_run=true', { method: 'POST' });
                if (!r1.ok) { alert('Bereinigungs-Check fehlgeschlagen'); return; }
                const d1 = await r1.json();
                if (!d1.matched) { alert('Keine defekten Einträge gefunden — alles sauber.'); return; }
                if (!window.confirm(
                  `${d1.matched} Einträge mit leerem Titel oder zu kurzem Inhalt gefunden. ` +
                  `Diese jetzt unwiderruflich löschen?`
                )) return;
                const r2 = await authFetch('/api/knowledge/_/cleanup-empty?dry_run=false', { method: 'POST' });
                if (!r2.ok) { alert('Löschen fehlgeschlagen'); return; }
                const d2 = await r2.json();
                alert(`${d2.deleted} defekte Einträge gelöscht.`);
                load();
              }}
              className="bg-white border border-amber-500 text-amber-600 hover:bg-amber-50 text-sm"
              data-testid="btn-kb-cleanup"
              title="Findet und löscht Einträge mit leerem Titel oder zu kurzem Inhalt"
            >
              <AlertTriangle className="w-4 h-4 mr-1" /> Bereinigen
            </Button>
          )}
          <Button onClick={startNew} className={`bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm ${isReadOnly ? 'hidden' : ''}`} data-testid="btn-kb-new"><Plus className="w-4 h-4 mr-1" /> Neuer Eintrag</Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <Input placeholder="Suchen (Titel, Inhalt, Tag)..." value={filter.search} onChange={e => setFilter(p => ({ ...p, search: e.target.value }))} className="pl-9 bg-white border-gray-300 text-sm" data-testid="kb-search" />
        </div>
        <select value={filter.category} onChange={e => setFilter(p => ({ ...p, category: e.target.value }))} className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="kb-filter-cat">
          <option value="">Alle Kategorien</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filter.tag} onChange={e => setFilter(p => ({ ...p, tag: e.target.value }))} className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="kb-filter-tag">
          <option value="">Alle Tags</option>
          {availableTags.map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
        </select>
        {(filter.tag || filter.category) && (
          <button onClick={() => setFilter({ category: '', search: filter.search, tag: '' })}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2"
                  data-testid="kb-clear-filters">
            ✕ Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Tag-Quick-Aktionen — sichtbar sobald ein Tag-Filter aktiv ist */}
      {filter.tag && !isReadOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900 flex items-center justify-between flex-wrap gap-2"
             data-testid="kb-tag-bulk-bar">
          <span>
            <strong>{filtered.length}</strong> Eintrag(e) mit Tag „<strong>{filter.tag}</strong>" — Massenaktion verfügbar.
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100"
                    onClick={async () => {
                      const newTag = window.prompt(`Tag „${filter.tag}" auf allen ${filtered.length} Einträgen umbenennen zu:`, filter.tag);
                      if (!newTag || newTag.trim() === filter.tag) return;
                      for (const e of filtered) {
                        const newTags = (e.tags || []).map(t => t === filter.tag ? newTag.trim() : t);
                        await authFetch(`/api/knowledge/${e.id}`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ tags: newTags }),
                        });
                      }
                      setFilter(p => ({ ...p, tag: newTag.trim() }));
                      load();
                    }}
                    data-testid="kb-tag-rename">
              Tag umbenennen
            </Button>
            <Button variant="outline" className="h-7 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                    onClick={async () => {
                      if (!window.confirm(`Wirklich ALLE ${filtered.length} Einträge mit Tag „${filter.tag}" löschen?`)) return;
                      for (const e of filtered) {
                        await authFetch(`/api/knowledge/${e.id}`, { method: 'DELETE' });
                      }
                      setFilter(p => ({ ...p, tag: '' }));
                      load();
                    }}
                    data-testid="kb-tag-bulk-delete">
              Alle löschen
            </Button>
          </div>
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm" data-testid="kb-editor">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-sm font-semibold text-[#32373c]">{editing === 'new' ? 'Neuer Eintrag' : 'Eintrag bearbeiten'}</h2>
            <button onClick={() => { setEditing(null); setForm(emptyEntry()); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><Label className="text-xs text-gray-500">Titel</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="mt-1 bg-white border-gray-300 text-sm" data-testid="kb-title-input" /></div>
            <div><Label className="text-xs text-gray-500">Kategorie</Label><select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><Label className="text-xs text-gray-500">Produktfamilie</Label><select value={form.productFamily || ''} onChange={e => setForm(p => ({ ...p, productFamily: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">{FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}</select></div>
            <div><Label className="text-xs text-gray-500">Sichtbarkeit</Label><select value={form.visibility} onChange={e => setForm(p => ({ ...p, visibility: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm">{VISIBILITIES.map(v => <option key={v} value={v}>{v}</option>)}</select></div>
            <div><Label className="text-xs text-gray-500">Tags (kommagetrennt)</Label><Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} className="mt-1 bg-white border-gray-300 text-sm" placeholder="z.B. fehler, portal, kommunikation" /></div>
            <div className="md:col-span-2"><Label className="text-xs text-gray-500">Modell-Codes (kommagetrennt, z. B. &bdquo;HS 5, HS 6 PV duo&ldquo;)</Label><Input value={form.modelCodes} onChange={e => setForm(p => ({ ...p, modelCodes: e.target.value }))} className="mt-1 bg-white border-gray-300 text-sm" placeholder="HS 3, HS 5, HS 5 PV duo" data-testid="kb-modelcodes-input" /></div>
            <div><Label className="text-xs text-gray-500">Sicherheitslevel</Label><select value={form.safetyLevel || ''} onChange={e => setForm(p => ({ ...p, safetyLevel: e.target.value || null }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"><option value="">Keines</option>{SAFETY_LEVELS.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}</select></div>
          </div>
          <div className="mb-3">
            <Label className="text-xs text-gray-500">Inhalt</Label>
            <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="mt-1 w-full min-h-[120px] rounded-md border border-gray-300 bg-white p-3 text-sm resize-y" data-testid="kb-content-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="kb-save">Speichern</Button>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(emptyEntry()); }} className="text-sm">Abbrechen</Button>
          </div>
        </div>
      )}

      {/* Entry list */}
      {loading ? <p className="text-sm text-gray-400">Laden...</p> : (
        <div className="space-y-2">
          {filtered.map(entry => (
            <div key={entry.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}>
                <div className="flex-1 min-w-0">
                  {/* Zeile 1: Titel — eigene Zeile damit er nicht von den
                      Modell-Code-Badges auf 0 Breite gequetscht wird. */}
                  <div className="text-sm font-medium text-[#32373c] truncate" data-testid={`kb-title-${entry.id}`}>
                    {(() => {
                      const t = (entry.title || '').trim();
                      if (t) return t;
                      const docTitle = entry.sourceDocument?.docTitle;
                      return (
                        <span className="italic text-amber-600" title="Dieser Eintrag hat keinen Titel — bitte über 'Bearbeiten' nachpflegen.">
                          {docTitle ? `(Eintrag ohne Titel — aus „${docTitle}")` : '(Eintrag ohne Titel)'}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Zeile 2: Badges — gemeinsam mit Modell-Codes, begrenzt
                      auf 8 sichtbare; Rest als „+N weitere". */}
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{entry.category}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#9ec043]/10 text-[#9ec043]">{entry.productFamily}</span>
                    {(entry.modelCodes || []).slice(0, 8).map(mc => (
                      <span key={mc} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">{mc}</span>
                    ))}
                    {(entry.modelCodes || []).length > 8 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50/60 text-emerald-700 border border-emerald-100 italic"
                            title={(entry.modelCodes || []).slice(8).join(', ')}>
                        +{(entry.modelCodes || []).length - 8} weitere
                      </span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">{entry.visibility}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!isReadOnly && (
                    <>
                      <button onClick={e => { e.stopPropagation(); startEdit(entry); }} className="p-1 text-gray-400 hover:text-[#9ec043]" data-testid={`kb-edit-${entry.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={e => { e.stopPropagation(); remove(entry.id); }} className="p-1 text-gray-400 hover:text-red-500" data-testid={`kb-delete-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                  {expanded === entry.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>
              {expanded === entry.id && (
                <div className="px-3 pb-3 border-t border-gray-100 pt-2">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{entry.content}</p>
                  {entry.tags?.length > 0 && <div className="flex gap-1 flex-wrap mt-2">{entry.tags.map(t => (
                    <button key={t}
                            onClick={(e) => { e.stopPropagation(); setFilter(p => ({ ...p, tag: t })); }}
                            className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${filter.tag === t ? 'bg-[#9ec043]/20 text-[#7a9c34] font-medium' : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
                            title={`Nach Tag „${t}" filtern`}
                            data-testid={`kb-tag-chip-${t}`}>
                      #{t}
                    </button>
                  ))}</div>}
                  {entry.safetyLevel && <div className="mt-2 text-[10px] text-amber-600">Sicherheitslevel: {entry.safetyLevel}</div>}
                  <div className="text-[10px] text-gray-300 mt-1">v{entry.version} | {entry.updatedAt?.slice(0, 10)} | {entry.createdBy}</div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Keine Einträge gefunden</p>}
        </div>
      )}
    </div>
  );
}
